import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Fuel, Phone } from "lucide-react";
import { PageHeader } from "./fordon";

const URL_SELF = "https://www.akhusbilfritid.se/verktyg/bransleforbrukning-husbil";

const FAQ = [
  {
    q: "Hur mycket drar en husbil per mil?",
    a: "En modern plåthusbil (Rapido V62, Ducato 140 hk) drar cirka 0,9–1,2 l/mil i landsvägsfart. Halvintegrerade landar på 1,1–1,4 l/mil. Integrerade flaggskepp (9005 DFH, 180 hk) drar 1,3–1,7 l/mil. Motorväg i 110 km/h ökar förbrukningen med 10–20 %.",
  },
  {
    q: "Vad kostar det att köra husbil 100 mil?",
    a: "Med 1,2 l/mil och dieselpris 18 kr/l: 120 l × 18 kr = 2 160 kr per 100 mil. Använd räknaren ovan för att räkna på ditt fordon och aktuellt bränslepris.",
  },
  {
    q: "Vad påverkar bränsleförbrukningen mest?",
    a: "1) Vikt — full vatten, gasol, cyklar, förvaring drar upp förbrukningen 10–15 %. 2) Hastighet — 90 km/h är sweet spot; 110 km/h ökar med 15–25 %. 3) Motorstorlek — 180 hk drar mer än 140 hk även i låg fart. 4) Vind och terräng — motvind eller berg kan lägga på 20 %.",
  },
  {
    q: "Är diesel eller elhusbil billigare att köra?",
    a: "Diesel är fortfarande billigast per mil (2026). Elhusbilar finns i begränsat sortiment och räckvidden begränsar användbarheten på långresor. Vi rekommenderar diesel för alla som kör över 500 mil per år.",
  },
];

export const Route = createFileRoute("/verktyg/bransleforbrukning-husbil")({
  head: () => ({
    meta: [
      { title: "Bränsleförbrukning husbil — räkna kostnad per mil | AK Husbil" },
      { name: "description", content: "Räkna ut bränsleförbrukning och körkostnad för din husbil. Fyll i förbrukning, bränslepris och sträcka — se totalkostnaden direkt." },
      { name: "keywords", content: "bränsleförbrukning husbil, husbil förbrukning, husbil bränsle, körkostnad husbil, husbil per mil" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Bränsleförbrukning husbil — räkna direkt" },
      { property: "og:description", content: "Räkna körkostnad per mil och per resa för din husbil." },
      { property: "og:url", content: URL_SELF },
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "Räkna bränsleförbrukning för husbil",
          description: "Så räknar du ut vad husbilen kostar att köra per mil och per resa.",
          totalTime: "PT2M",
          step: [
            { "@type": "HowToStep", name: "Kolla förbrukningen", text: "Se husbilens genomsnittliga förbrukning i färddatorn eller från tidigare tankningar. Räkna 0,9–1,7 l/mil beroende på modell." },
            { "@type": "HowToStep", name: "Ta aktuellt bränslepris", text: "Kolla dagens dieselpris i kr per liter. 2026 ligger snittet runt 17–19 kr." },
            { "@type": "HowToStep", name: "Räkna sträckan", text: "Uppskatta din resa i antal mil. En rundresa Kalix–Stockholm är ca 190 mil enkel väg." },
            { "@type": "HowToStep", name: "Multiplicera", text: "Förbrukning × bränslepris × mil = total bränslekostnad för resan." },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
    ],
  }),
  component: Page,
});

function fmt(n: number): string {
  return new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(n);
}

function Page() {
  const [consumption, setConsumption] = useState(1.2);
  const [price, setPrice] = useState(18);
  const [miles, setMiles] = useState(100);

  const perMil = useMemo(() => consumption * price, [consumption, price]);
  const total = useMemo(() => perMil * miles, [perMil, miles]);
  const perYear = useMemo(() => perMil * 800, [perMil]);

  return (
    <>
      <PageHeader
        label="Verktyg · Räkna hemma"
        title={<>Bränsleförbrukning <span className="font-italic-display text-accent">husbil</span>.</>}
        sub="Räkna ut vad husbilen kostar att köra — per mil, per resa och per år. Realistiska värden baserat på våra kunders faktiska tankningar."
      />

      <main className="max-w-3xl mx-auto px-4 py-10 space-y-10">
        <section className="rounded-2xl border-2 border-primary/20 bg-card p-6 md:p-8">
          <div className="flex items-center gap-2 mb-6">
            <Fuel className="text-primary" />
            <h2 className="text-xl font-bold">Räkna körkostnad</h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-3">
            <label className="block">
              <span className="text-sm font-medium">Förbrukning (l/mil)</span>
              <input type="number" step="0.1" min="0.5" max="3" value={consumption} onChange={(e) => setConsumption(+e.target.value)} className="mt-1 w-full rounded-lg border p-2 text-base" />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Bränslepris (kr/l)</span>
              <input type="number" step="0.1" min="10" max="40" value={price} onChange={(e) => setPrice(+e.target.value)} className="mt-1 w-full rounded-lg border p-2 text-base" />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Sträcka (mil)</span>
              <input type="number" step="10" min="1" value={miles} onChange={(e) => setMiles(+e.target.value)} className="mt-1 w-full rounded-lg border p-2 text-base" />
            </label>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            <div className="rounded-lg bg-muted p-4">
              <div className="text-xs text-muted-foreground">Kostnad per mil</div>
              <div className="text-2xl font-bold">{fmt(perMil)} kr</div>
            </div>
            <div className="rounded-lg bg-muted p-4">
              <div className="text-xs text-muted-foreground">Total resa ({miles} mil)</div>
              <div className="text-2xl font-bold">{fmt(total)} kr</div>
            </div>
            <div className="rounded-lg bg-muted p-4">
              <div className="text-xs text-muted-foreground">Per år (800 mil)</div>
              <div className="text-2xl font-bold">{fmt(perYear)} kr</div>
            </div>
          </div>
        </section>

        <section className="prose prose-neutral dark:prose-invert max-w-none">
          <h2>Realistiska riktvärden per modell</h2>
          <table>
            <thead><tr><th>Modell</th><th>Förbrukning (l/mil)</th><th>Anmärkning</th></tr></thead>
            <tbody>
              <tr><td>Rapido V55 (140 hk)</td><td>0,9–1,1</td><td>Kompakt plåthusbil</td></tr>
              <tr><td>Rapido V62 (140 hk)</td><td>0,9–1,2</td><td>Vanligaste plåtis</td></tr>
              <tr><td>Rapido V68 (140–180 hk)</td><td>1,0–1,3</td><td>Enkelsängar</td></tr>
              <tr><td>Rapido 866F (150–180 hk)</td><td>1,1–1,4</td><td>Halvintegrerad, dubbla golv</td></tr>
              <tr><td>Rapido 9005 DFH (180 hk)</td><td>1,3–1,7</td><td>Integrerad flaggskepp</td></tr>
            </tbody>
          </table>
          <p>Motorväg i 110 km/h lägger på 15–25 % ovanpå värden. Full last (vatten, gasol, cyklar) 10–15 %.</p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-6">Vanliga frågor</h2>
          <div className="space-y-4">
            {FAQ.map((f, i) => (
              <div key={i} className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">{f.q}</h3>
                <p className="text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-xl bg-primary text-primary-foreground p-8 text-center">
          <h2 className="text-2xl font-bold mb-3">Frågor om just din husbil?</h2>
          <p className="mb-6 opacity-90">Ring så räknar vi tillsammans — vi vet vad våra kunder faktiskt drar.</p>
          <a href="tel:0708629743" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-background text-foreground font-semibold hover:opacity-90">
            <Phone className="w-4 h-4" /> 070-862 97 43
          </a>
        </section>
      </main>
    </>
  );
}
